<template>
  <AdminLayout>
    <div class="admin-dashboard">
      <h2>仪表盘概览</h2>

      <!-- 统计卡片 -->
      <section class="stats-section">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">🎫</div>
            <div class="stat-content">
              <h3>总卡密数</h3>
              <div class="stat-number">{{ stats.totalCards }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">✅</div>
            <div class="stat-content">
              <h3>已激活卡密</h3>
              <div class="stat-number">{{ stats.activatedCards }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">👥</div>
            <div class="stat-content">
              <h3>有效用户</h3>
              <div class="stat-number">{{ stats.validUsers }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">📈</div>
            <div class="stat-content">
              <h3>今日激活</h3>
              <div class="stat-number">{{ stats.todayActivations }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 最近活动 -->
      <section class="activities-section">
        <h3>最近活动</h3>
        <div class="activity-container">
          <div v-if="loading" class="loading-state">
            <p>加载中...</p>
          </div>
          <div v-else-if="recentActivities.length === 0" class="empty-state">
            <p>暂无最新活动</p>
          </div>
          <div v-else class="activity-list">
            <div
              v-for="activity in recentActivities"
              :key="activity.id"
              class="activity-item"
            >
              <div class="activity-icon">📧</div>
              <div class="activity-content">
                <div class="activity-text">{{ activity.description }}</div>
                <div class="activity-time">{{ activity.formatted_time }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 快速操作 -->
      <section class="quick-actions-section">
        <h3>快速操作</h3>
        <div class="action-grid">
          <router-link to="/admin/config" class="action-card">
            <div class="action-icon">⚙️</div>
            <div class="action-content">
              <div class="action-title">配置管理</div>
              <div class="action-desc">管理BioRender自动化账户配置</div>
            </div>
            <div class="action-arrow">→</div>
          </router-link>

          <router-link to="/admin/cards" class="action-card">
            <div class="action-icon">🎫</div>
            <div class="action-content">
              <div class="action-title">卡密管理</div>
              <div class="action-desc">创建和管理激活码</div>
            </div>
            <div class="action-arrow">→</div>
          </router-link>

          <button @click="loadDashboardData" class="action-card refresh-btn" :disabled="loading">
            <div class="action-icon">🔄</div>
            <div class="action-content">
              <div class="action-title">刷新数据</div>
              <div class="action-desc">重新加载最新的统计信息</div>
            </div>
          </button>
        </div>
      </section>

      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/admin/AdminLayout.vue'
import { getDashboardStats, getRecentActivities } from '@/api/dashboard'

export default {
  name: 'AdminDashboard',
  components: {
    AdminLayout
  },
  data() {
    return {
      stats: {
        totalCards: 0,
        activatedCards: 0,
        validUsers: 0,
        todayActivations: 0
      },
      recentActivities: [],
      loading: false,
      message: '',
      messageType: 'success'
    }
  },
  mounted() {
    this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        // 并行加载统计数据和最新活动
        const [statsRes, activitiesRes] = await Promise.all([
          getDashboardStats(),
          getRecentActivities(10)
        ])

        if (statsRes.code === 200) {
          this.stats = statsRes.data
        } else {
          console.error('加载统计数据失败:', statsRes.message)
          this.showMessage('加载统计数据失败', 'error')
        }

        if (activitiesRes.code === 200) {
          this.recentActivities = activitiesRes.data || []
        } else {
          console.error('加载活动数据失败:', activitiesRes.message)
          this.showMessage('加载活动数据失败', 'error')
        }

        if (statsRes.code === 200 || activitiesRes.code === 200) {
          this.showMessage('数据刷新成功', 'success')
        }
      } catch (error) {
        console.error('加载仪表盘数据失败:', error)
        this.showMessage('加载仪表盘数据失败: ' + error.message, 'error')
      } finally {
        this.loading = false
      }
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.admin-dashboard h2 {
  text-align: center;
  font-size: var(--font-size-xxl);
  margin-bottom: var(--spacing-xxl);
  color: var(--text-primary);
}

.admin-dashboard h3 {
  font-size: var(--font-size-xl);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.stats-section,
.activities-section,
.quick-actions-section {
  margin-bottom: var(--spacing-xxl);
}

/* 统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-xl);
}

.stat-card {
  background: var(--bg-white);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.stat-icon {
  font-size: 2.5rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color), var(--info-color));
  border-radius: 50%;
  color: white;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-content h3 {
  color: var(--text-secondary);
  font-size: var(--font-size-md);
  margin-bottom: var(--spacing-xs);
  font-weight: 500;
  border: none;
  padding: 0;
}

.stat-number {
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
}

/* 活动列表 */
.activity-container {
  background: var(--bg-white);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-light);
  min-height: 200px;
}

.activity-list {
  display: flex;
  flex-direction: column;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-light);
  transition: background-color var(--transition-fast);
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-item:hover {
  background-color: var(--bg-primary);
}

.activity-item .activity-icon {
  font-size: var(--font-size-xl);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  border-radius: 50%;
  flex-shrink: 0;
}

.activity-content {
  flex: 1;
}

.activity-text {
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  font-weight: 500;
}

.activity-time {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.loading-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: var(--text-muted);
  font-style: italic;
}

/* 快速操作 */
.action-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-xl);
}

.action-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-xl);
  background: var(--bg-white);
  border: 2px solid var(--border-light);
  border-radius: var(--border-radius-lg);
  text-decoration: none;
  color: var(--text-primary);
  transition: all var(--transition-normal);
  cursor: pointer;
}

.action-card:hover {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.action-card.refresh-btn {
  border: 2px solid var(--success-color);
}

.action-card.refresh-btn:hover {
  border-color: var(--success-hover);
  background-color: var(--bg-success);
}

.action-card.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.action-card .action-icon {
  font-size: 2rem;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  flex-shrink: 0;
}

.action-content {
  flex: 1;
}

.action-title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  color: var(--text-primary);
}

.action-desc {
  color: var(--text-secondary);
  font-size: var(--font-size-md);
  line-height: 1.4;
}

.action-arrow {
  font-size: var(--font-size-lg);
  color: var(--text-muted);
  transition: transform var(--transition-fast);
}

.action-card:hover .action-arrow {
  transform: translateX(4px);
  color: var(--primary-color);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .action-grid {
    grid-template-columns: 1fr;
  }

  .stat-card,
  .action-card {
    padding: var(--spacing-lg);
  }

  .activity-item {
    padding: var(--spacing-md);
  }
}

@media (max-width: 480px) {
  .stat-card {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }

  .action-card {
    flex-direction: column;
    text-align: center;
  }

  .action-arrow {
    display: none;
  }
}
</style>